/* eslint-disable */
import { useLoginContext } from "../../../App";
import i18n from "../../../i18n";
import { useEffect, useState, useCallback } from "react";

const useB2BTranslation = (module, locale = null) => {
    const { loginData } = useLoginContext() || {};
    const [isReady, setIsReady] = useState(false);
    const [currentLocale, setCurrentLocale] = useState(null);
    
    // Get locale with better fallback logic
    const getLocaleToUse = useCallback(() => {
        if (locale) return locale;
        if (loginData?.locale) return loginData.locale;
        
        // Better browser language detection for Firefox compatibility
        try {
            const browserLang = navigator.language || navigator.languages?.[0] || 'es';
            return browserLang.split('-')[0];
        } catch (error) {
            console.warn('useB2BTranslation: Error getting browser language, falling back to "es"');
            return 'es';
        }
    }, [locale, loginData?.locale]);

    // Check if i18n resources are loaded
    useEffect(() => {
        const localeToUse = getLocaleToUse();
        
        const checkResourcesLoaded = async () => {
            try {
                // Wait for i18n to be initialized
                if (!i18n.isInitialized) {
                    await new Promise((resolve) => {
                        const onInitialized = () => {
                            i18n.off('initialized', onInitialized);
                            resolve();
                        };
                        i18n.on('initialized', onInitialized);
                        // Fallback timeout in case initialized event never fires
                        setTimeout(resolve, 5000);
                    });
                }

                // Ensure the locale is supported
                const supportedLanguages = i18n.options.supportedLngs || ['es', 'ca', 'en'];
                const finalLocale = supportedLanguages.includes(localeToUse) ? localeToUse : 'es';
                
                // Check if the specific namespace and language are loaded
                const hasResource = i18n.hasResourceBundle(finalLocale, module);
                if (!hasResource) {
                    // Load the resource if not available
                    await Promise.all([
                        i18n.loadNamespaces(module),
                        i18n.loadLanguages(finalLocale)
                    ]);
                    
                    // Wait a bit for resources to be actually available
                    await new Promise(resolve => setTimeout(resolve, 100));
                }
                
                // Change language if needed
                if (i18n.language !== finalLocale) {
                    await i18n.changeLanguage(finalLocale);
                }
                
                setCurrentLocale(finalLocale);
                setIsReady(true);
            } catch (error) {
                console.error('useB2BTranslation: Error loading resources:', error);
                // Fallback: set to Spanish and mark as ready
                setCurrentLocale('es');
                setIsReady(true);
            }
        };

        setIsReady(false);
        checkResourcesLoaded();
    }, [getLocaleToUse, module]);

    const t = useCallback((key) => {
        if (!isReady || !currentLocale) {
            // Return the key itself while loading (instead of undefined)
            return key;
        }

        try {
            // Use the translation with proper locale and namespace
            const translation = i18n.t(key, { lng: currentLocale, ns: module });
            
            // If translation is the same as key, it means no translation was found
            // Try with default namespace as fallback
            if (translation === key && module !== 'other') {
                return i18n.t(key, { lng: currentLocale, ns: 'other' });
            }
            
            return translation;
        } catch (error) {
            console.warn(`useB2BTranslation: Translation error for key "${key}" in locale "${currentLocale}":`, error);
            return key; // Fallback to key
        }
    }, [isReady, currentLocale, module]);

    return { t, locale: currentLocale, isReady };
};

export default useB2BTranslation;